import { useEffect, useState } from "react";
import { DollarSign, TrendingUp, Send, CheckCircle2, Undo2, Loader2, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { toast } from "sonner";

const fmt = (n) => "$" + Number(n || 0).toLocaleString();

const Stat = ({ label, value, icon: Icon, accent }) => (
  <div className={`rounded-2xl p-6 border ${accent ? "bg-[#0D1B2A] text-white border-[#0D1B2A]" : "bg-white text-[#0D1B2A] border-[#0D1B2A]/10"}`}>
    <div className={`inline-grid place-items-center h-10 w-10 rounded-xl ${accent ? "bg-white/10 text-[#E85D04]" : "bg-[#FFFBF5] text-[#E85D04]"}`}><Icon className="h-5 w-5" /></div>
    <div className="mt-5 font-display text-2xl md:text-3xl font-semibold tracking-tight">{value}</div>
    <div className={`text-xs mt-1 ${accent ? "text-white/60" : "text-[#0D1B2A]/60"}`}>{label}</div>
  </div>
);

const AdminFinance = () => {
  const { api, admin } = useAdminAuth();
  const [summary, setSummary] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [pledges, setPledges] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [range, setRange] = useState({ from: "", to: "" });
  const [pledgeQuery, setPledgeQuery] = useState("");
  const [refundModal, setRefundModal] = useState(null);
  const [refundReason, setRefundReason] = useState("");
  const [busy, setBusy] = useState(false);
  const isSuper = admin?.role === "super_admin";

  const loadAll = async () => {
    const q = { ...(range.from && { from: range.from }), ...(range.to && { to: range.to }) };
    const [s, p, pl, r] = await Promise.all([
      api.get("/admin/finance/summary", { params: q }).then((x) => x.data),
      api.get("/admin/payouts").then((x) => x.data),
      api.get("/admin/pledges/search", { params: { q: pledgeQuery } }).then((x) => x.data),
      api.get("/admin/refunds").then((x) => x.data),
    ]);
    setSummary(s); setPayouts(p); setPledges(pl); setRefunds(r);
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [range.from, range.to]);

  const approvePayout = async (row) => {
    if (!isSuper) return toast.error("Super admin required");
    setBusy(true);
    try { await api.post(`/admin/payouts/${row.campaign_id}/approve`); toast.success(`Payout approved for ${row.campaign_title}`); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const submitRefund = async () => {
    if (!refundReason.trim()) return toast.error("Reason required");
    setBusy(true);
    try {
      await api.post(`/admin/pledges/${refundModal.pledge_id}/refund`, { reason: refundReason });
      toast.success("Refund logged"); setRefundModal(null); setRefundReason(""); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="admin-finance-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E85D04]">Finance</span>
          <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold tracking-tight">Financial oversight</h1>
          <p className="mt-1 text-sm text-[#0D1B2A]/60">Platform-wide revenue, payouts, and refunds.</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-xs text-[#0D1B2A]/60">From</label>
            <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="h-10 rounded-xl border-[#0D1B2A]/15" data-testid="finance-from" />
          </div>
          <div>
            <label className="text-xs text-[#0D1B2A]/60">To</label>
            <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="h-10 rounded-xl border-[#0D1B2A]/15" data-testid="finance-to" />
          </div>
        </div>
      </div>

      {summary && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="GMV in range" value={fmt(summary.gmv)} icon={DollarSign} accent />
          <Stat label={`Platform fee (${summary.platform_fee_pct}%)`} value={fmt(summary.fee_revenue)} icon={TrendingUp} />
          <Stat label="Pending payouts" value={fmt(summary.pending_payouts.total)} icon={Send} />
          <Stat label="Completed payouts" value={fmt(summary.completed_payouts.total)} icon={CheckCircle2} />
        </div>
      )}

      {/* Payout Queue */}
      <div className="mt-10 rounded-2xl bg-white border border-[#0D1B2A]/10 overflow-hidden">
        <div className="p-5 border-b border-[#0D1B2A]/5">
          <h2 className="font-display text-lg font-semibold">Payout queue</h2>
          <p className="text-xs text-[#0D1B2A]/60 mt-0.5">Ended or funded campaigns eligible for payout.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FFFBF5] text-[#0D1B2A]/50 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left px-5 py-3">Campaign</th>
                <th className="text-left px-5 py-3">Creator</th>
                <th className="text-right px-5 py-3">Raised</th>
                <th className="text-right px-5 py-3">Fee</th>
                <th className="text-right px-5 py-3">Net</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-right px-5 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#0D1B2A]/5">
              {payouts.length === 0 && <tr><td className="px-5 py-8 text-center text-[#0D1B2A]/50" colSpan={7}>No campaigns eligible for payout.</td></tr>}
              {payouts.map((p) => (
                <tr key={p.campaign_id} data-testid={`payout-row-${p.campaign_id}`} className="hover:bg-[#FFFBF5]/60">
                  <td className="px-5 py-3 font-semibold text-[#0D1B2A]">{p.campaign_title}</td>
                  <td className="px-5 py-3 text-[#0D1B2A]/80">{p.creator_name}</td>
                  <td className="px-5 py-3 text-right font-medium">{fmt(p.amount_gross)}</td>
                  <td className="px-5 py-3 text-right text-[#0D1B2A]/60">−{fmt(p.fee)}</td>
                  <td className="px-5 py-3 text-right font-semibold">{fmt(p.amount_net)}</td>
                  <td className="px-5 py-3">
                    <span className={`text-xs rounded-full px-2 py-0.5 ${p.payout_status === "completed" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{p.payout_status}</span>
                    {p.approved_by_email && <div className="text-[10px] text-[#0D1B2A]/50 mt-1">by {p.approved_by_email}</div>}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button disabled={busy || !isSuper || p.payout_status === "completed"} onClick={() => approvePayout(p)}
                      data-testid={`approve-payout-${p.campaign_id}`}
                      className="inline-flex items-center gap-1 rounded-lg bg-[#E85D04] hover:bg-[#c94d02] text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-40">
                      {p.payout_status === "completed" ? "Paid" : "Approve payout"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Refund Log */}
      <div className="mt-10 rounded-2xl bg-white border border-[#0D1B2A]/10 overflow-hidden">
        <div className="p-5 border-b border-[#0D1B2A]/5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold">Refund log</h2>
            <p className="text-xs text-[#0D1B2A]/60 mt-0.5">Search pledges by backer email, campaign title or pledge id.</p>
          </div>
          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#0D1B2A]/40" />
              <Input value={pledgeQuery} onChange={(e) => setPledgeQuery(e.target.value)}
                data-testid="pledge-search" placeholder="email / title / id" className="pl-9 h-10 w-64 rounded-xl border-[#0D1B2A]/15" />
            </div>
            <button onClick={loadAll} className="rounded-xl px-3 h-10 text-sm border border-[#0D1B2A]/15 hover:bg-[#0D1B2A]/5">Search</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FFFBF5] text-[#0D1B2A]/50 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left px-5 py-3">Backer</th>
                <th className="text-left px-5 py-3">Campaign</th>
                <th className="text-right px-5 py-3">Amount</th>
                <th className="text-left px-5 py-3">Date</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-right px-5 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#0D1B2A]/5">
              {pledges.length === 0 && <tr><td className="px-5 py-8 text-center text-[#0D1B2A]/50" colSpan={6}>No pledges found.</td></tr>}
              {pledges.map((p) => (
                <tr key={p.pledge_id} data-testid={`pledge-row-${p.pledge_id}`}>
                  <td className="px-5 py-3">
                    <div className="font-medium">{p.backer.name || "—"}</div>
                    <div className="text-xs text-[#0D1B2A]/60">{p.backer.email}</div>
                  </td>
                  <td className="px-5 py-3 text-[#0D1B2A]/80">{p.campaign.title}</td>
                  <td className="px-5 py-3 text-right font-medium">{fmt(p.amount)}</td>
                  <td className="px-5 py-3 text-xs text-[#0D1B2A]/60">{new Date(p.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-3"><span className={`text-xs rounded-full px-2 py-0.5 ${p.status === "refunded" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>{p.status}</span></td>
                  <td className="px-5 py-3 text-right">
                    {p.status !== "refunded" ? (
                      <button disabled={!isSuper} onClick={() => { setRefundModal(p); setRefundReason(""); }}
                        data-testid={`refund-${p.pledge_id}`}
                        className="inline-flex items-center gap-1 rounded-lg border border-[#0D1B2A]/15 text-[#0D1B2A]/80 hover:bg-[#0D1B2A]/5 px-3 py-1.5 text-xs font-semibold disabled:opacity-40">
                        <Undo2 className="h-3.5 w-3.5" /> Refund
                      </button>
                    ) : <span className="text-xs text-[#0D1B2A]/50">{p.refund?.reason ? `"${p.refund.reason}"` : "refunded"}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {refunds.length > 0 && (
          <div className="p-5 border-t border-[#0D1B2A]/5 text-xs text-[#0D1B2A]/60">
            {refunds.length} refund{refunds.length > 1 ? "s" : ""} on record · total {fmt(refunds.reduce((a, r) => a + (r.amount || 0), 0))}
          </div>
        )}
      </div>

      <Dialog open={!!refundModal} onOpenChange={(o) => !o && setRefundModal(null)}>
        <DialogContent className="sm:max-w-[520px] rounded-2xl" data-testid="refund-modal">
          <DialogHeader><DialogTitle className="font-display">Refund pledge</DialogTitle></DialogHeader>
          <p className="text-sm text-[#0D1B2A]/70">
            Refunding {fmt(refundModal?.amount)} to <strong>{refundModal?.backer?.name}</strong> for <em>{refundModal?.campaign?.title}</em>.
          </p>
          <Textarea rows={4} value={refundReason} onChange={(e) => setRefundReason(e.target.value)}
            placeholder="Reason for the refund…" data-testid="refund-reason-input"
            className="rounded-xl border-[#0D1B2A]/15" />
          <div className="flex justify-end gap-2">
            <button onClick={() => setRefundModal(null)} className="rounded-xl px-4 py-2 text-sm border border-[#0D1B2A]/15">Cancel</button>
            <button onClick={submitRefund} disabled={busy || !refundReason.trim()}
              data-testid="refund-submit"
              className="rounded-xl bg-[#E85D04] hover:bg-[#c94d02] text-white px-4 py-2 text-sm font-semibold disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm refund"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFinance;
